import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency } from './formatCurrency';

/**
 * Generates a professional PDF report for the ERP system
 * @param {string} reportType - "sales", "inventory", or "profit"
 * @param {Array} data - The report data
 * @param {Object} summary - The summary data (revenue, expenses, etc.)
 */
export const generateReportPDF = (reportType, data, summary) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    // -- Header & Branding --
    try {
        doc.addImage('/IMG_5468.PNG', 'PNG', 20, 10, 45, 12);
    } catch (e) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(110, 60, 180);
        doc.text("Promotional", 20, 25);
        doc.setFontSize(22);
        doc.setTextColor(180, 180, 180);
        doc.text("LLC-FZ", 68, 25);
    }

    // Company Details (Top Right)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const companyInfo = [
        "www.promotional.ae",
        "info@promotional.ae",
        "052-3622578",
        "Dubai, U.A.E."
    ];
    let currentY = 15;
    companyInfo.forEach(line => {
        doc.text(line, pageWidth - 20, currentY, { align: "right" });
        currentY += 3.5;
    });

    // Report Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    const titles = {
        sales: "SALES TREND REPORT",
        inventory: "STOCK STATUS REPORT",
        profit: "PRODUCT PROFITABILITY REPORT",
        teamPerformance: "TEAM PERFORMANCE ANALYTICS"
    };
    doc.text(titles[reportType] || "BUSINESS REPORT", 20, 40);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on: ${dateStr}`, 20, 46);

    // -- Executive Summary Section --
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, 55, pageWidth - 40, 30, 2, 2, "F");
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text("EXECUTIVE SUMMARY", 25, 62);

    const summaryY = 73;
    const colWidth = (pageWidth - 50) / 4;

    // Revenue
    doc.setFontSize(8);
    doc.text("TOTAL REVENUE", 25, summaryY - 4);
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text(formatCurrency(summary.totalRevenue || 0), 25, summaryY + 2);

    // Expenses
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("TOTAL EXPENSES", 25 + colWidth, summaryY - 4);
    doc.setFontSize(11);
    doc.setTextColor(220, 38, 38); // Red
    doc.text(formatCurrency(summary.totalExpenses || 0), 25 + colWidth, summaryY + 2);

    // Profit
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("NET PROFIT", 25 + (colWidth * 2), summaryY - 4);
    doc.setFontSize(11);
    doc.setTextColor(22, 163, 74); // Green
    doc.text(formatCurrency(summary.netProfit || 0), 25 + (colWidth * 2), summaryY + 2);

    // Tax
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("TAX PAYABLE", 25 + (colWidth * 3), summaryY - 4);
    doc.setFontSize(11);
    doc.setTextColor(234, 88, 12); // Orange
    doc.text(formatCurrency(summary.taxPayable || 0), 25 + (colWidth * 3), summaryY + 2);

    // -- Data Table --
    let tableHead = [];
    let tableBody = [];

    if (reportType === 'sales') {
        tableHead = [['Month', 'Sales (AED)', 'Cost (AED)', 'Profit (AED)']];
        tableBody = data.map(item => [
            item.month,
            (item.sales || 0).toLocaleString(),
            (item.cost || 0).toLocaleString(),
            (item.profit || 0).toLocaleString()
        ]);
    } else if (reportType === 'inventory') {
        tableHead = [['Item Name', 'In Stock', 'Quantity Sold']];
        tableBody = data.map(item => [
            item.item,
            item.inStock || 0,
            item.sold || 0
        ]);
    } else if (reportType === 'profit') {
        tableHead = [['Product', 'Sold Qty', 'Revenue', 'Cost', 'Profit', 'Margin (%)']];
        tableBody = data.map(item => [
            item.product,
            item.totalQuantity,
            formatCurrency(item.totalRevenue),
            formatCurrency(item.totalCost),
            formatCurrency(item.profit),
            `${item.margin.toFixed(1)}%`
        ]);
    } else if (reportType === 'teamPerformance') {
        tableHead = [['Team Member', 'Orders', 'Revenue', 'Avg Order', 'Profit']];
        tableBody = data.map(item => [
            item.name,
            item.orderCount,
            formatCurrency(item.totalRevenue),
            formatCurrency(item.avgOrderValue),
            formatCurrency(item.totalProfit)
        ]);
    } else if (reportType === 'expense') {
        tableHead = [['Date', 'Category', 'Vendor', 'Amount (AED)', 'Status']];
        tableBody = data.map(item => [
            new Date(item.date).toLocaleDateString(),
            item.category,
            item.vendor,
            formatCurrency(item.amount),
            item.status
        ]);
    } else if (reportType === 'invoiceMonthly') {
        tableHead = [['Invoice #', 'Date', 'Customer', 'Amount (AED)', 'Tax (AED)', 'Total (AED)', 'Status']];
        tableBody = data.map(item => [
            item.orderId,
            new Date(item.date).toLocaleDateString(),
            item.customer,
            formatCurrency(item.amount - (item.tax || 0)),
            formatCurrency(item.tax || 0),
            formatCurrency(item.amount),
            item.status
        ]);
    }

    autoTable(doc, {
        startY: 95,
        head: tableHead,
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 8.5, cellPadding: 3 },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 20, right: 20 }
    });

    // -- Footer --
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("This is an automatically generated business report from Promotional ERP System.", pageWidth / 2, doc.internal.pageSize.height - 15, { align: "center" });

    doc.save(`${reportType}_report_${new Date().getTime()}.pdf`);
};
